import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sheetSyncApi, type SheetBindingCreate } from '@/api/sheet-sync';

const BINDINGS = ['sheet-bindings'];
const PENDING = ['sheet-pending'];
const PENDING_COUNT = ['sheet-pending-count'];
const RUNS = ['sheet-runs'];
const UNMATCHED = ['sheet-unmatched-keys'];

export function useSheetBindings() {
  return useQuery({ queryKey: BINDINGS, queryFn: sheetSyncApi.listBindings });
}

export function useSheetPending() {
  return useQuery({ queryKey: PENDING, queryFn: sheetSyncApi.listPending });
}

/**
 * Backs the header badge. Polled, because the value changes from an external push rather
 * than from anything the user did in this tab — nothing else would invalidate it.
 */
export function useSheetPendingCount() {
  return useQuery({
    queryKey: PENDING_COUNT,
    queryFn: sheetSyncApi.pendingCount,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  });
}

export function useSheetRuns() {
  return useQuery({ queryKey: RUNS, queryFn: sheetSyncApi.listRuns });
}

export function useUnmatchedKeys() {
  return useQuery({ queryKey: UNMATCHED, queryFn: sheetSyncApi.unmatchedKeys });
}

/** Approving or rejecting can move the projection, so invalidate it too. */
function useSyncMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      [BINDINGS, PENDING, PENDING_COUNT, UNMATCHED, ['projection']].forEach(queryKey =>
        qc.invalidateQueries({ queryKey })
      );
    },
  });
}

export function useAttachBinding() {
  return useSyncMutation((data: SheetBindingCreate) => sheetSyncApi.attach(data));
}

export function useDetachBinding() {
  return useSyncMutation((id: number) => sheetSyncApi.detach(id));
}

export function useUpdateBinding() {
  return useSyncMutation(({ id, data }: { id: number; data: Partial<SheetBindingCreate> }) =>
    sheetSyncApi.updateBinding(id, data)
  );
}

export function useApprovePending() {
  return useSyncMutation(({ id, note }: { id: number; note?: string }) =>
    sheetSyncApi.approve(id, note)
  );
}

export function useRejectPending() {
  return useSyncMutation(({ id, note }: { id: number; note?: string }) =>
    sheetSyncApi.reject(id, note)
  );
}

/**
 * Is this entity field driven by the sheet?
 *
 * Used to lock bound fields in the edit forms. Without the lock, a manual edit would be
 * silently reverted by the next sync — the fastest way to lose trust in the whole feature.
 */
export function useBindingFor(
  targetType: string,
  targetId: number | undefined,
  targetField: string
) {
  const { data: bindings } = useSheetBindings();
  if (targetId == null) return undefined;
  return bindings?.find(
    b =>
      b.is_active &&
      b.target_type === targetType &&
      b.target_id === targetId &&
      b.target_field === targetField
  );
}
